// assets/modal.js

class ModalComponent extends HTMLElement {
  constructor() {
    super();
    this._previouslyFocusedElement = null;
    this.attachShadow({ mode: "open" });

    if (this.shadowRoot)
      this.shadowRoot.innerHTML = `
      <style>
        :host {
          position: fixed;
          inset: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1000;
          display: flex;
          justify-content: center;
          align-items: center;

          opacity: 1;
          visibility: visible;
          pointer-events: auto;
          transition: opacity 0.3s ease, visibility 0.3s ease;
        }

        :host(.is-hidden) {
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
        }
      </style>
      <div class="modal-content">
        <slot></slot>
      </div>
    `;
  }

  connectedCallback() {
    /** ✅ Read data attributes HERE */
    this.hasVideo = this.hasAttribute("data-video");
    this.autoplayVideo = this.hasAttribute("data-autoplay");

    if (this.hasAttribute("open")) {
      this.show();
    }

    const closeButton = this.querySelector(".modal-close-button");
    closeButton?.addEventListener("click", () => this.hide());

    this.addEventListener("click", (event) => {
      if (event.target === this) this.hide();
    });

    this._keydownEventListener = (event) => {
      if (event.key === "Escape") this.hide();
    };
    this.addEventListener("keydown", this._keydownEventListener);

    const links = document.querySelectorAll(`a[href="#${this.id}"]`);
    links.forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        this.show();
      });
    });
  }

  disconnectedCallback() {
    this._restoreFocus();
    this.removeEventListener("keydown", this._keydownEventListener);
  }

  show() {
    this._previouslyFocusedElement = document.activeElement;

    this.classList.remove("is-hidden");
    this.setAttribute("open", "true");
    document.body.classList.add("modal-open");

    this._trapFocus();
    this._setInitialFocus();

    this.dispatchEvent(
      new CustomEvent("modal-show", { bubbles: true, composed: true }),
    );

    this.querySelectorAll("deferred-media").forEach((media) => {
      const video = media.querySelector("video");
      const autoplayVideo = media.hasAttribute("data-autoplay");
      if (video && autoplayVideo) {
        video.muted = true;
        video.playsInline = true;

        video.play().catch(() => {
          console.warn("Autoplay blocked by browser");
        });
      }
    });
  }

  hide() {
    this.classList.add("is-hidden");
    this.removeAttribute("open");
    document.body.classList.remove("modal-open");

    this._restoreFocus();

    this.dispatchEvent(
      new CustomEvent("modal-hide", { bubbles: true, composed: true }),
    );

    this.querySelectorAll("video").forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });
  }

  _trapFocus() {
    this._focusEventListener = (event) => {
      const focusableElements = this._getFocusableElements();
      if (!focusableElements.length) return;

      if (!this.contains(event.target)) {
        focusableElements[0].focus();
      }
    };
    this.addEventListener("focusin", this._focusEventListener);
  }

  _setInitialFocus() {
    this._getFocusableElements()[0]?.focus();
  }

  _getFocusableElements() {
    return Array.from(
      this.querySelectorAll(
        'button, video, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    );
  }

  _restoreFocus() {
    this.removeEventListener("focusin", this._focusEventListener);
    if (this._previouslyFocusedElement) {
      this._previouslyFocusedElement.focus();
    }
  }
}

if (!customElements.get("modal-component")) {
  customElements.define("modal-component", ModalComponent);
}

export default ModalComponent;
